// dashboard/index.js

import React from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate } from "react-router-dom";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { VisibilityOutlined } from "@mui/icons-material";
import axios from "../../../api";
import dayjs from "dayjs";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

// ---------- INTERFACES ----------

interface AssetTypeOption {
  asset_type_id: number;
  asset_type_name: string;
}

interface OrderRow {
  order_id: number;
  request_id: string;
  ticket_service_id: string;
  asset_type_name: string;
  spare_part_name: string;
  order_number: string;
  order_date: string;
  order_status: string;
  total_price: number;
  customer_name: string;
  [key: string]: any;
}

interface TableMetaData {
  rowData: any[];
  rowIndex: number;
}

// ---------- EXTRACTED COMPONENTS ----------

interface ProductCellProps {
  value: string;
  sparePart: string;
}

const ProductCell: React.FC<ProductCellProps> = ({ value, sparePart }) => (
  <Box>
    <Typography variant="subtitle2" fontWeight="bold">
      {value}
    </Typography>
    <Typography variant="caption" color="textSecondary">
      {sparePart}
    </Typography>
  </Box>
);

interface OrderDateCellProps {
  value: string;
}

const OrderDateCell: React.FC<OrderDateCellProps> = ({ value }) => {
  if (!value) return <>-</>;
  return <>{dayjs(value).format("MMM DD, YYYY")}</>;
};

interface StatusCellProps {
  value: string;
}

const StatusCell: React.FC<StatusCellProps> = ({ value }) => {
  const getStatusColor = (status: string): string => {
    switch (status) {
      case "Delivered":
        return "#e1f2f2";
      case "Shipped":
        return "#1976d2";
      case "Pending":
        return "#fdf5e1";
      case "Cancelled":
        return "#fceddd";
      default:
        return "#9e9e9e";
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: getStatusColor(value),
        color: "#000",
        padding: "4px 8px",
        borderRadius: "8px",
        textAlign: "center",
        display: "inline-block",
        minWidth: "80px",
      }}
    >
      {value}
    </Box>
  );
};

interface TotalPriceCellProps {
  value: number;
}

const formatCurrency = (value?: number | string) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) {
    return `$${value}`;
  }
  return `$${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const TotalPriceCell: React.FC<TotalPriceCellProps> = ({ value }) => (
  <span style={{ fontWeight: "bold" }}>{formatCurrency(value)}</span>
);

interface ActionsCellProps {
  orderId: number;
  onView: (orderId: number) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({ orderId, onView }) => (
  <IconButton color="primary" onClick={() => onView(orderId)}>
    <VisibilityOutlined
      sx={{
        color: "var(--primary-color, #2e7d32) !important",
      }}
    />
  </IconButton>
);

const ORDER_STATUS_OPTIONS = ["Pending", "Shipped", "Delivered", "Cancelled"] as const;

const OrderList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState<OrderRow[]>([]);
  const [assetTypes, setAssetTypes] = React.useState<AssetTypeOption[]>([]);
  const [ticketIdFilter, setTicketIdFilter] = React.useState<string | null>(null);
  const [ticketIdInput, setTicketIdInput] = React.useState("");
  const [assetTypeFilter, setAssetTypeFilter] = React.useState<AssetTypeOption | null>(null);
  const [orderDateFrom, setOrderDateFrom] = React.useState<string>("");
  const [orderDateTo, setOrderDateTo] = React.useState<string>("");
  const [statusFilter, setStatusFilter] = React.useState<string>("");

  const uniqueTicketIds = React.useMemo(() => {
    const set = new Set(rows.map((r) => r.ticket_service_id).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  const fetchAssetTypes = React.useCallback(async () => {
    try {
      const res = await axios.get("/asset-types");
      const list = res.data?.list ?? [];
      setAssetTypes(
        list.map((a: AssetTypeOption) => ({
          asset_type_id: a.asset_type_id,
          asset_type_name: a.asset_type_name ?? "",
        }))
      );
    } catch (e) {
      console.error("Failed to fetch asset types", e);
    }
  }, []);

  React.useEffect(() => {
    fetchAssetTypes();
  }, [fetchAssetTypes]);

  const filteredRows = React.useMemo(() => {
    let result = rows;
    const activeTicketFilter = ticketIdFilter || ticketIdInput;
    if (activeTicketFilter) {
      result = result.filter(
        (r) =>
          r.ticket_service_id &&
          String(r.ticket_service_id)
            .toLowerCase()
            .includes(String(activeTicketFilter).toLowerCase())
      );
    }
    if (assetTypeFilter) {
      result = result.filter(
        (r) => r.asset_type_name && r.asset_type_name === assetTypeFilter.asset_type_name
      );
    }
    if (orderDateFrom) {
      const fromStart = dayjs(orderDateFrom).startOf("day");
      result = result.filter(
        (r) => r.order_date && !dayjs(r.order_date).isBefore(fromStart, "day")
      );
    }
    if (orderDateTo) {
      const toEnd = dayjs(orderDateTo).endOf("day");
      result = result.filter(
        (r) => r.order_date && !dayjs(r.order_date).isAfter(toEnd, "day")
      );
    }
    if (statusFilter) {
      result = result.filter((r) => r.order_status === statusFilter);
    }
    return result;
  }, [rows, ticketIdFilter, assetTypeFilter, orderDateFrom, orderDateTo, statusFilter]);

  const hasActiveFilters =
    ticketIdFilter ||
    ticketIdInput ||
    assetTypeFilter ||
    orderDateFrom ||
    orderDateTo ||
    statusFilter;
  const clearFilters = () => {
    setTicketIdFilter(null);
    setTicketIdInput("");
    setAssetTypeFilter(null);
    setOrderDateFrom("");
    setOrderDateTo("");
    setStatusFilter("");
  };

  const handleViewOrder = (orderId: number) => {
    navigate(`/view-order/${orderId}`);
  };

  // Render functions for table columns
  const renderProductCell = (value: string, tableMeta: TableMetaData) => {
    const sparePart =
      tableMeta.rowData[0] === value
        ? filteredRows[tableMeta.rowIndex]?.spare_part_name
        : "";
    return <ProductCell value={value} sparePart={sparePart} />;
  };

  const renderOrderDate = (value: string) => {
    return <OrderDateCell value={value} />;
  };

  const renderStatusCell = (value: string) => {
    return <StatusCell value={value} />;
  };

  const renderTotalPrice = (value: number) => {
    return <TotalPriceCell value={value} />;
  };

  const renderActionsCell = (dataIndex: number) => {
    const order = filteredRows[dataIndex];
    return order ? <ActionsCell orderId={order.order_id} onView={handleViewOrder} /> : null;
  };

  const columns: MUIDataTableColumn[] = React.useMemo(
    () => [
      { name: "request_id", label: "Request ID" },
      { name: "ticket_service_id", label: "Ticket ID" },
      {
        name: "asset_type_name",
        label: "Product",
        options: {
          customBodyRender: renderProductCell,
        },
      },
      { name: "order_number", label: "Order ID" },
      {
        name: "order_date",
        label: "Order Date",
        options: {
          customBodyRender: renderOrderDate,
        },
      },
      {
        name: "order_status",
        label: "Status",
        options: {
          customBodyRender: renderStatusCell,
        },
      },
      {
        name: "total_price",
        label: "Total",
        options: {
          customBodyRender: renderTotalPrice,
        },
      },
      { name: "customer_name", label: "Customer" },
      {
        name: "actions",
        label: "Actions",
        options: {
          customBodyRenderLite: renderActionsCell,
          sort: false,
          filter: false,
        },
      },
    ],
    [rows, filteredRows]
  );

  const tableOptions: MUIDataTableOptions = {
    selectableRows: "none",
    selectableRowsHeader: false,
    elevation: 0,
    tableBodyHeight: "auto",
    responsive: "standard",
  };

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get("/spare-order");
      setRows(response?.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card sx={{ p: 3, m: "16px auto", maxWidth: "2000px" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1.5, mb: 2 }}>
          <FormControl sx={{ width: 200, minWidth: 200 }}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
              Ticket ID
            </SoftTypography>
            <Autocomplete
              options={uniqueTicketIds}
              value={ticketIdFilter}
              inputValue={ticketIdInput}
              onInputChange={(_, v) => setTicketIdInput(v)}
              onChange={(_, v) => {
                setTicketIdFilter(v);
                setTicketIdInput(v ?? "");
              }}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder="All"
                />
              )}
            />
          </FormControl>
          <FormControl sx={{ width: 170, minWidth: 170 }}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
              Order date from
            </SoftTypography>
            <TextField
              type="date"
              size="small"
              value={orderDateFrom}
              onChange={(e) => setOrderDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiInputBase-root": { height: 40 } }}
            />
          </FormControl>
          <FormControl sx={{ width: 170, minWidth: 170 }}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
              Order date to
            </SoftTypography>
            <TextField
              type="date"
              size="small"
              value={orderDateTo}
              onChange={(e) => setOrderDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiInputBase-root": { height: 40 } }}
            />
          </FormControl>
          <FormControl sx={{ width: 160, minWidth: 160 }}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
              Status
            </SoftTypography>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{ height: 40 }}
            >
              <MenuItem value="">All</MenuItem>
              {ORDER_STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ width: 200, minWidth: 200 }}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
              Product (Asset Type)
            </SoftTypography>
            <Autocomplete
              options={assetTypes}
              getOptionLabel={(o) => o.asset_type_name}
              value={assetTypeFilter}
              onChange={(_, v) => setAssetTypeFilter(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder="All"
                />
              )}
            />
          </FormControl>
          {hasActiveFilters && (
            <Button variant="outlined" color="primary" size="small" onClick={clearFilters}>
              Clear Filter
            </Button>
          )}
        </Box>
        <SoftBox>
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Orders"}
              data={filteredRows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
        </SoftBox>
      </Card>
    </DashboardLayout>
  );
};

export default OrderList;
